import React, {useReducer, useRef, useEffect} from 'react'
import {Button, Card, Colors, H3, IconButton, ListItem, P, Popup, Row, SortableList, Panel} from '../../UI/index.js'
import CarrierLogo from '../../parcels/CarrierLogo.js'
import {formSpacing, FormRow, Input} from '../../UI/forms/index.js'
import EditShipppingRuleModal, {EditShipppingRuleModalRef} from '../../settings/shippingRules/EditShippingRuleModal.js'
import {Reseller, ShippingRule} from '../../../../server/functions/resellers/resellers.types.js'
import {ParcelCarrier} from '../../../../server/functions/parcels/carrier.types.js'
import guid from '../../../../utils/guid.js'
import t from '../../../helpers/Languages.js'
import type {PopupRef} from '../../UI/overlays/Popup.js'

interface EditShippingRuleSetProps {
    reseller: Reseller
    carriers: ParcelCarrier[]
    shippingRuleSet: Reseller['settings']['parcels']['shippingRules'][0]
    onSave: (shippingRuleSet: Reseller['settings']['parcels']['shippingRules'][0]) => void
    onCancel: () => void
}

interface ShippingRulesState {
    shippingRuleSet: Reseller['settings']['parcels']['shippingRules'][0]
}

const EditShippingRuleSet = (props: EditShippingRuleSetProps) => {
    const {reseller, carriers, onSave} = props

    const [state, setState] = useReducer((state: ShippingRulesState, newState: Partial<ShippingRulesState>) => ({...state, ...newState}), {
        shippingRuleSet: props.shippingRuleSet
    })

    const modalRef = useRef<EditShipppingRuleModalRef>(null)
    const popupRef = useRef<PopupRef>(null)

    useEffect(() => {
        if (!props.shippingRuleSet.name) {
            document.getElementById('shippingRuleSetName')?.focus()
        }

        setState({shippingRuleSet: props.shippingRuleSet})
    }, [props.shippingRuleSet])

    const addShippingRule = () => {
        const newShippingRule: ShippingRule = {
            id: guid.generate(),
            name: '',
            active: true,
            matchAllConditions: true,
            conditionGroups: [[{
                variable: 'options',
                operator: 'contains',
                value: []
            }]],
            carrier: ''
        }

        const updatedShippingRuleSet = {
            ...state.shippingRuleSet,
            shippingRules: [newShippingRule, ...state.shippingRuleSet.shippingRules]
        }

        setState({shippingRuleSet: updatedShippingRuleSet})
        onSave(updatedShippingRuleSet)

        modalRef.current?.open(newShippingRule)
    }

    const removeShippingRule = (event, shippingRule: ShippingRule) => {
        event.stopPropagation()

        popupRef.current?.open('Verzendregel verwijderen', 'Weet je zeker dat je deze wilt verwijderen?', () => {
            const updatedShippingRuleSet = {
                ...state.shippingRuleSet,
                shippingRules: state.shippingRuleSet.shippingRules.filter((rule) => rule.id !== shippingRule.id)
            }
            setState({shippingRuleSet: updatedShippingRuleSet})
            onSave(updatedShippingRuleSet)

            popupRef.current?.close()
        })
    }

    return (
        <Card shadow style={{display: 'flex', flexDirection: 'column', padding: 20, width: 600, height: 'fit-content', maxHeight: 'calc(100vh - 120px)'}}>
            <H3 style={{marginBottom: 24}}>{`Verzendregels - ${state.shippingRuleSet?.name}`}</H3>

            <P>De verzendregels worden van boven naar beneden doorlopen. De eerste regel die voldoet wordt toegepast. Wanneer er geen verzendregels van toepassing zijn dan wordt gekeken naar de algemene voorkeuren.</P>
            <P style={{marginTop: 12}}>{`Een vervoerder die in de voorkeuren een 'X' heeft bij een specifieke verzendoptie+land zal hiervoor nooit worden gebruikt.`}</P>
            <FormRow style={{marginTop: formSpacing.section + formSpacing.rowGap, marginBottom: formSpacing.rowGap}}>
                <Input
                    id='shippingRuleSetName'
                    label='Naam'
                    value={state.shippingRuleSet.name}
                    required
                    onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const updatedShippingRuleSet = {...state.shippingRuleSet, name: event.target.value}
                        setState({shippingRuleSet: updatedShippingRuleSet})
                        onSave(updatedShippingRuleSet)
                    }}
                />
            </FormRow>

            {state.shippingRuleSet.shippingRules.length > 0 &&
                <Panel style={{marginTop: 36, flex: 1, overflowY: 'auto'}}>
                    <SortableList
                        items={state.shippingRuleSet.shippingRules || []}
                        onChange={(shippingRules: ShippingRule[]) => {
                            const updatedShippingRuleSet = {...state.shippingRuleSet, shippingRules}
                            setState({shippingRuleSet: updatedShippingRuleSet})
                            onSave(updatedShippingRuleSet)
                        }}
                        renderItem={(shippingRule: ShippingRule) => {
                            const carrier = carriers.find((carrier) => carrier.name === shippingRule.carrier)
                            let carrierName = shippingRule.carrier

                            if (carrier) {
                                carrierName = carrier.displayName || carrier.name

                                carrierName = `${carrierName}${carrier.platform ? ` - ${carrier.platform}` : ''}`
                            }

                            return (
                                <ListItem
                                    inactive={!shippingRule.active}
                                    onClick={() => {
                                        modalRef.current?.open(shippingRule)
                                    }}
                                    icon='mdi mdi-drag-horizontal'
                                >

                                    <P ellipsis>
                                        {shippingRule.name}
                                    </P>

                                    <div style={{flex: 1}} />

                                    <Row style={{alignItems: 'center', width: 200, marginLeft: 12}}>
                                        <div style={{width: 64, flexShrink: 0}}>
                                            <CarrierLogo
                                                disabled={!shippingRule.active}
                                                carriers={carriers}
                                                value={shippingRule.carrier}
                                                size={32}
                                            />
                                        </div>

                                        <P ellipsis style={{width: 150, marginLeft: 12, color: !shippingRule.active ? Colors.textMedium : undefined}}>
                                            {carrierName}
                                        </P>
                                    </Row>

                                    <IconButton
                                        style={{marginLeft: 6}}
                                        icon='mdi mdi-delete'
                                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => removeShippingRule(event, shippingRule)}
                                    />
                                </ListItem>
                            )
                        }}
                    />
                </Panel>
            }

            <Button
                style={{marginTop: 24}}
                variant='outline'
                icon='mdi mdi-plus'
                label={t('shippingrule')}
                onClick={addShippingRule}
            />

            <EditShipppingRuleModal
                reseller={reseller}
                carriers={carriers}
                onChange={(shippingRule) => {
                    const index = state.shippingRuleSet.shippingRules.findIndex((rule) => rule.id === shippingRule.id)
                    const shippingRules = [...state.shippingRuleSet.shippingRules]
                    shippingRules[index] = shippingRule
                    const updatedShippingRulesSet = {...state.shippingRuleSet, shippingRules: shippingRules}
                    setState({shippingRuleSet: updatedShippingRulesSet})
                    onSave(updatedShippingRulesSet)
                }}
                ref={modalRef}
            />

            <Popup ref={popupRef} />
        </Card>
    )
}

export default EditShippingRuleSet
